import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { CastFigure } from '../../motion';
import { useReducedMotion } from '../../motion/hooks';
import { TAPE_RANKS } from '../../data/theTape';
import './tape-reveal.css';

// Rank-drop title sequence. Mounts only for /the-tape?reveal=N where N is ON THE RECORD in TAPE_RANKS —
// an unpublished rank never reveals anything. Timed like a cold open (~18 s), then lifts and lands on #rank-N.
// Reduced motion: no sequence, straight to the card.

const STEPS = [
  { key: 'live', at: 400 },       // live dot + LIVE DESK line
  { key: 'title', at: 1800 },     // TAPE №N IS IN
  { key: 'envelope', at: 4000 },  // sealed envelope drops in
  { key: 'tear', at: 5700 },      // seal cracks, flap tears open
  { key: 'figure', at: 7200 },    // the manager, in motion
  { key: 'card', at: 9600 },      // cream card: name / team / model line
  { key: 'headline', at: 12200 }, // the tape's headline
  { key: 'out', at: 16100 },      // letterbox lifts
  { key: 'done', at: 17900 },
];

export default function TapeReveal({ rank, onDone }) {
  const reduced = useReducedMotion();
  const navigate = useNavigate();
  const entry = useMemo(() => TAPE_RANKS.find((r) => r.rank === rank), [rank]);
  const [step, setStep] = useState('boot');
  const timers = useRef([]);

  const finish = useRef(() => {});
  finish.current = () => {
    timers.current.forEach(clearTimeout);
    onDone?.();
    navigate(`/the-tape#rank-${rank}`, { replace: true });
    const el = document.getElementById(`rank-${rank}`);
    if (el) el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!entry) { onDone?.(); return undefined; }
    if (reduced) { finish.current(); return undefined; }
    let reached = 'boot';
    timers.current = STEPS.map(({ key, at }) => setTimeout(() => {
      if (key === 'done') { finish.current(); return; }
      reached = key;
      setStep(reached);
    }, at));
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, reduced, rank]);

  if (!entry || reduced) return null;
  const idx = ['boot', 'live', 'title', 'envelope', 'tear', 'figure', 'card', 'headline', 'out'].indexOf(step);
  const on = (name, from) => (idx >= from ? name : '');

  return (
    <div className={`tr ${step === 'out' ? 'tr--out' : ''}`} role="dialog" aria-label={`Tape number ${rank} reveal`}>
      <div className="tr__bar tr__bar--top" aria-hidden="true" />
      <div className="tr__bar tr__bar--bottom" aria-hidden="true" />

      <div className="tr__stage">
        <div className={`tr__live ${on('is-on', 1)}`}>
          <span className="live-dot" aria-hidden="true" />
          Live desk · {`Tape drop`}
        </div>

        <h1 className={`tr__title ${on('is-on', 2)}`}>
          Tape <span className="tr__no">№{rank}</span> is in.
        </h1>

        <div className={`tr__envelope ${on('is-in', 3)} ${on('is-torn', 4)}`} aria-hidden="true">
          <div className="tr__flap" />
          <div className="tr__seal" />
        </div>

        <div className={`tr__figure ${on('is-on', 5)}`}>
          <CastFigure
            name={entry.manager}
            trigger="none"
            pose={idx >= 6 ? (rank <= 3 ? 'celebrate' : 'point') : 'shock'}
            alt={`${entry.manager} caricature`}
          />
        </div>

        <div className={`tr__card paper ${on('is-on', 6)}`}>
          <div className="tr__rank">{rank}</div>
          <div className="tr__who">
            <b>{entry.manager}</b>
            <i>{entry.teamName}</i>
          </div>
          <div className="tr__pct">{entry.teichPct || 'No % on the record yet'}</div>
        </div>

        <p className={`tr__headline ${on('is-on', 7)}`}>{entry.headline}</p>
      </div>

      <button type="button" className="tr__skip" onClick={() => finish.current()}>
        Skip to the tape
      </button>
    </div>
  );
}
